import * as Blockly from 'blockly';
import { javascriptGenerator } from 'blockly/javascript';
import '@blockly/field-dependent-dropdown'; // Registers 'field_dependent_dropdown' type
import { EventsStringService } from 'src/app/shared/services/events-string.service';
import { BLOCKLY_COLORS } from './blocky-types.types';

/**
 * Builds a categorized event mapping from the flat events list.
 * Returns: { categoryOptions, optionMapping }
 */
function buildCategorizedEventData(eventsList: [string, string][]): {
  categoryOptions: [string, string][];
  optionMapping: Record<string, [string, string][]>;
} {
  // Define category mappings based on event key prefixes
  const categoryDefs: { key: string; prefix: string; label: string }[] = [
    { key: 'nachricht', prefix: 'RAGAI.MSG.', label: '💬 Nachricht' },
    { key: 'sprache', prefix: 'RAGAI.AUDIO.', label: '🎤 Sprache / Audio' },
    { key: 'avatar', prefix: 'RAGAI.AVATAR.', label: '🧑🏻‍🏫 Avatar' },
    { key: 'ki_bild', prefix: 'RAGAI.AIIMAGE_OPENAI.', label: '🖼️ KI-Bild (OpenAI)' },
    { key: 'ki_bild_falai', prefix: 'RAGAI.AIIMAGE_FALAI.', label: '🖼️ KI-Bild (fal.ai)' },
    { key: 'ki_text', prefix: 'RAGAI.AITEXT_OPENAI.', label: '🗨️ KI-Text' },
    { key: 'overlay', prefix: 'RAGAI.OVERLAY.', label: '🔲 Overlay' },
    { key: 'button', prefix: 'RAGAI.BUTTON.', label: '🎛️ Button' },
    { key: 'stage', prefix: 'RAGAI.STAGE.', label: '📍 Stage' },
    { key: 'keyval', prefix: 'RAGAI.KEYVAL.', label: '🔑 KeyVal' },
    { key: 'projectkeyval', prefix: 'RAGAI.PROJECTKEYVAL.', label: '🔑 Projekt-KeyVal' },
    { key: 'webhook', prefix: 'RAGAI.WEBHOOK.', label: '⚓️ Webhook' },
    { key: 'mail', prefix: 'RAGAI.MAIL.', label: '📧 Mail' },
    { key: 'youtube', prefix: 'RAGAI.YOUTUBE.', label: '🎬 Youtube' },
    { key: 'vimeo', prefix: 'RAGAI.VIMEO.', label: '🎬 Vimeo' },
    { key: 'rise', prefix: 'RAGAI.RISE.', label: '📚 Rise' },
    { key: 'lxt', prefix: 'RAGAI.LXT.', label: '📚 LXT' },
    { key: 'scorm', prefix: 'RAGAI.SCORM.', label: '📦 SCORM' },
    { key: 'adobeconnect', prefix: 'RAGAI.ADOBECONNECT.', label: '🖥️ Adobe Connect' },
    { key: 'eventhook', prefix: 'RAGAI.EVENTS.', label: '⚙️ Eventhook' },
    { key: 'info', prefix: 'RAGAI.INFO.', label: 'ℹ️ Info' }
  ];

  const categoryOptions: [string, string][] = [];
  const optionMapping: Record<string, [string, string][]> = {};

  for (const cat of categoryDefs) {
    const events = eventsList.filter(([key]) =>
      (key.startsWith(cat.prefix) || key === cat.prefix.slice(0, -1)) &&
      !key.endsWith('.*') // Exclude wildcard "Alle" entries
    );

    if (events.length > 0) {
      categoryOptions.push([cat.label, cat.key]);
      // Map events to [label, value] for Blockly dropdown format
      optionMapping[cat.key] = events.map(([eventKey, eventLabel]) => {
        // Strip category prefix pattern: "emoji? Kategorie: " or "Kategorie: "
        let cleanLabel = eventLabel.replace(/^(?:[^\w\s]\s*)?[^:]+:\s*/, '') || eventLabel;
        // Remove any remaining emojis
        cleanLabel = cleanLabel.replace(/\p{Extended_Pictographic}\uFE0F?/gu, '').trim();
        return [cleanLabel, JSON.stringify(eventKey)] as [string, string];
      });
    }
  }

  return { categoryOptions, optionMapping };
}

/**
 * Registers the EVENTS_ON_CATEGORIZED block with dependent dropdowns.
 * Must be called once during initialization (from events.category.ts).
 */
let _registered = false;
export function registerCategorizedEventDropdown(eventStrings: EventsStringService): void {
  if (_registered) return;
  _registered = true;

  const eventsList = eventStrings.get();
  const { categoryOptions, optionMapping } = buildCategorizedEventData(eventsList);

  // Define the block using JSON API
  Blockly.defineBlocksWithJsonArray([
    {
      type: 'EVENT_CATEGORIZED',
      message0: '⚡ %1 %2',
      args0: [
        {
          type: 'field_dropdown',
          name: 'EVENT_CATEGORY',
          options: categoryOptions,
        },
        {
          type: 'field_dependent_dropdown',
          name: 'EVENT_NAME',
          parentName: 'EVENT_CATEGORY',
          optionMapping: optionMapping,
          defaultOptions: [['Kein Ereignis verfügbar', '"RAGAI.*"']],
        }
      ],
      output: 'EVENT',
      colour: BLOCKLY_COLORS.EVENTS_LIGHT,
      tooltip: 'Wähle ein Ereignis nach Kategorie aus.\n\n🆔 EVENT_CATEGORIZED',
    }
  ]);

  // Register JavaScript generator
  javascriptGenerator.forBlock['EVENT_CATEGORIZED'] = (block) => {
    const eventName = block.getFieldValue('EVENT_NAME');
    return [eventName, 0];
  };

  // Register Event Data block
  registerCategorizedEventDataBlock(eventsList);
}

/**
 * Builds the 2-level mapping: Manager → Fields
 * Each manager shows all possible fields that events of that manager can contain.
 */
function buildManagerFieldMapping(): {
  managerOptions: [string, string][];
  fieldMapping: Record<string, [string, string][]>;
} {
  const managerDefs: { key: string; label: string; fields: [string, string][] }[] = [
    {
      key: 'nachricht', label: '💬 Nachricht',
      fields: [['Text', 'text'], ['Prompt', 'prompt'], ['Input', 'input']],
    },
    {
      key: 'stage', label: '📍 Stage',
      fields: [['Name', 'name'], ['Titel', 'title'], ['ID', '_id'], ['Versteckt', 'hidden'], ['Skills', 'skills'], ['Index', 'index'], ['Von (from)', 'from'], ['Nach (to)', 'to']],
    },
    {
      key: 'keyval', label: '🔑 KeyVal',
      fields: [['Schlüssel', 'key'], ['Wert', 'value'], ['Typ', 'type']],
    },
    {
      key: 'projectkeyval', label: '🔑 Projekt-KeyVal',
      fields: [['Schlüssel', 'key'], ['Wert', 'value'], ['Typ', 'type']],
    },
    {
      key: 'webhook', label: '⚓️ Webhook',
      fields: [['Skill-ID', 'skillId'], ['Anfrage', 'request'], ['Ergebnis', 'result']],
    },
    {
      key: 'overlay', label: '🔲 Overlay',
      fields: [['Text', 'text'], ['HTML', 'html'], ['Buttons', 'buttons'], ['Overlay-Element', 'overlay']],
    },
    {
      key: 'button', label: '🎛️ Button',
      fields: [['Text', 'text'], ['Event-Daten (komplett)', 'eventData']],
    },
    {
      key: 'sprache', label: '🎤 Sprache / Audio',
      fields: [['Status', 'state'], ['Stream', 'stream'], ['Nachricht', 'message'], ['Text', 'text']],
    },
    {
      key: 'youtube', label: '🎬 Youtube',
      fields: [['Player', 'player'], ['Aktueller Status', 'currentState'], ['Position (Sekunden)', 'currentPlayPosition']],
    },
    {
      key: 'vimeo', label: '🎬 Vimeo',
      fields: [['Player', 'player'], ['Position (Sekunden)', 'currentPlayPosition']],
    },
    {
      key: 'rise', label: '📚 Rise',
      fields: [['Target', 'target'], ['RISE-Objekt', 'RISE']],
    },
    {
      key: 'lxt', label: '📚 LXT',
      fields: [['Target', 'target'], ['LXT-Objekt', 'LXT']],
    },
    {
      key: 'adobeconnect', label: '🖥️ Adobe Connect',
      fields: [['User', 'user'], ['User-ID', 'userId'], ['Status', 'status'], ['Rolle', 'role'], ['Play-Status', 'playState'], ['Pod-Titel', 'podTitle'], ['Breite', 'width'], ['Höhe', 'height'], ['Nachricht', 'msgVal']],
    },
    {
      key: 'ki_bild', label: '🖼️ KI-Bild',
      fields: [['Ergebnis', 'result'], ['Prompt', 'prompt']],
    },
    {
      key: 'ki_text', label: '🗨️ KI-Text',
      fields: [['Ergebnis', 'result'], ['Input', 'input']],
    },
    {
      key: 'mail', label: '📧 Mail',
      fields: [['Empfänger', 'to'], ['Betreff', 'subject'], ['Inhalt', 'body']],
    },
    {
      key: 'scorm', label: '📦 SCORM',
      fields: [['Schlüssel', 'key'], ['Wert', 'value']],
    },
    {
      key: 'eventhook', label: '⚙️ Event',
      fields: [['Hook', 'hook'], ['ID', 'id']],
    }
  ];

  const managerOptions: [string, string][] = [];
  const fieldMapping: Record<string, [string, string][]> = {};

  for (const mgr of managerDefs) {
    managerOptions.push([mgr.label, mgr.key]);
    fieldMapping[mgr.key] = mgr.fields;
  }

  return { managerOptions, fieldMapping };
}

/**
 * Registers the EVENT_DATA_CATEGORIZED block with 2 dropdowns: Manager → Feld.
 */
function registerCategorizedEventDataBlock(_eventsList: [string, string][]): void {
  const { managerOptions, fieldMapping } = buildManagerFieldMapping();

  Blockly.defineBlocksWithJsonArray([
    {
      type: 'EVENT_DATA_CATEGORIZED',
      message0: '🔻 Ereigniswerte: %1 . %2',
      args0: [
        {
          type: 'field_dropdown',
          name: 'DATA_MANAGER',
          options: managerOptions,
        },
        {
          type: 'field_dependent_dropdown',
          name: 'DATA_FIELD',
          parentName: 'DATA_MANAGER',
          optionMapping: fieldMapping,
          defaultOptions: [['(kein Feld)', 'undefined']],
        }
      ],
      output: 'String',
      colour: BLOCKLY_COLORS.EVENTS_LIGHT,
      tooltip: 'Greife auf ein Feld der Ereignis-Daten (eventData) zu.\nWähle den Manager und dann das gewünschte Datenfeld.\n\n🆔 EVENT_DATA_CATEGORIZED',
    }
  ]);

  // Register JavaScript generator
  javascriptGenerator.forBlock['EVENT_DATA_CATEGORIZED'] = (block) => {
    const field = block.getFieldValue('DATA_FIELD');
    const code = `RAGAI.EVENTS._datafromEvent(eventName,eventData,"${field}")`;
    return [code, 0];
  };
}
